import React, { useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { getCurrentUser } from 'features/auth/ducks/selectors'
import { updateUserRoutine } from 'features/auth/ducks/actions'
import { useSelector, useDispatch } from 'react-redux'
import { pathOr, propOr } from 'ramda'
import { isNotNilOrEmpty } from 'utils/ramda'
import Avatar from 'components/Avatar'
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded'
import { userRole } from 'utils/userRoles'
import DetailsSection from 'features/bpDetails/Components/BpInfo/DetailsSection'
import avatarService from 'services/avatarService'

const SummaryItem = ({ title, value, variant }) => {
  return (
    <SummaryItemWrapper>
      <SummaryItemTitle>{title}</SummaryItemTitle>
      <SummaryItemValueWrapper>
        <SummaryItemValue variant={variant}>{value}</SummaryItemValue>
        <SummaryItemTotal>in total</SummaryItemTotal>
      </SummaryItemValueWrapper>
    </SummaryItemWrapper>
  )
}

const ProfileInfo = () => {
  const user = useSelector(getCurrentUser)
  const userFirstName = propOr('', 'firstName', user)
  const userLastName = propOr('', 'lastName', user)
  const currentUserSource = pathOr('', ['source', 'value'], user)
  const isFromInfoExchange = currentUserSource === 'info_exchange'
  const avatarUrl = useMemo(() => pathOr(null, ['avatar'], user), [user])
  const rawDocumentsCount =
    propOr(null, 'documentsCount', user) ??
    propOr(null, 'documents_count', user) ??
    0
  const documentsCount = useMemo(() => {
    const value = Number(rawDocumentsCount)
    if (!Number.isFinite(value)) return '0'
    return new Intl.NumberFormat().format(value)
  }, [rawDocumentsCount])

  const dispatch = useDispatch()
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const hasAvatar = isNotNilOrEmpty(avatarUrl) && !isFromInfoExchange

  const openFilePicker = () => {
    if (!uploading && !isFromInfoExchange) fileInputRef.current?.click()
  }

  const handleAvatarChange = async e => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file (JPG, PNG or WebP).')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('That image is too large — please choose one under 5 MB.')
      return
    }
    setUploading(true)
    try {
      const res = await avatarService.upload(file)
      const newAvatar = res?.data?.data?.avatar
      if (newAvatar) dispatch(updateUserRoutine.success({ avatar: newAvatar }))
      toast.success('Profile photo updated.')
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not upload your photo. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      <Title>
        Your profile
      </Title>
      <UserAvatar>
        <AvatarButton
          type='button'
          onClick={openFilePicker}
          disabled={isFromInfoExchange || uploading}
          title={isFromInfoExchange
            ? 'Your photo is managed by your account provider'
            : hasAvatar ? 'Change your photo' : 'Add a photo'}
        >
          <Avatar
            src={hasAvatar ? avatarUrl : null}
            firstName={userFirstName}
            lastName={userLastName}
            email={user.email}
            size={84}
          />
          {!isFromInfoExchange && (
            <AvatarOverlay>
              {uploading ? '…' : <PhotoCameraRoundedIcon style={{ fontSize: 20 }} />}
            </AvatarOverlay>
          )}
        </AvatarButton>
        <HiddenInput
          ref={fileInputRef}
          type='file'
          accept='image/png,image/jpeg,image/webp'
          onChange={handleAvatarChange}
        />
      </UserAvatar>
      {!isFromInfoExchange && !hasAvatar && (
        <AvatarNudge type='button' onClick={openFilePicker}>
          Add a profile photo so colleagues recognise you
        </AvatarNudge>
      )}
      <UserName>
        {`${userFirstName} ${userLastName}`}
      </UserName>
      <UserEmail>
        {user.email}
      </UserEmail>
      <UserRole>
        {userRole(user)}
      </UserRole>
      <Details>
        {
          user.type !== 'emergency_service' && (
            <DetailsSection label='Summary'>
              <SummaryWrapper>
                <SummaryItem
                  title='Building Passports'
                  value={propOr('', 'buildingPassportsCount', user)}
                />
                <SummaryItem
                  title='Documents'
                  value={documentsCount}
                  variant='primary'
                />
              </SummaryWrapper>
            </DetailsSection>
          )
        }
        {
          user.discount > 0 &&
            <DetailsSection label='Discount information'>
              <Discount>
                Your Building Passport discount:
                <span>{user.discount}%</span>
              </Discount>
            </DetailsSection>
        }
      </Details>
    </div>
  )
}

export default ProfileInfo

const Title = styled.div`
  font-size: 14px;
  text-transform: uppercase;
`

const UserAvatar = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 30px 0 16px;

  img {
    border-radius: 50%;
    min-width: 84px;
    max-width: 84px;
    min-height: 84px;
    max-height: 84px;
  }
`

const AvatarOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(12, 18, 41, 0.5);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s ease;
`

const AvatarButton = styled.button`
  position: relative;
  border: none;
  background: none;
  padding: 0;
  border-radius: 50%;
  display: inline-flex;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

  &:hover ${AvatarOverlay} {
    opacity: ${({ disabled }) => (disabled ? 0 : 1)};
  }
`

const HiddenInput = styled.input`
  display: none;
`

const AvatarNudge = styled.button`
  font: inherit;
  display: block;
  margin: 0 auto 8px;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
`

const UserName = styled.div`
  font-size: 23px;
  color: ${({ theme }) => theme.colors.haiti};
  text-align: center;
`

const UserEmail = styled.div`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-align: center;
  margin: 3px 0;
`

const UserRole = styled.div`
  font-size: 17px;
  color: ${({ theme }) => theme.colors.haiti};
  opacity: .7;
  text-align: center;
`

const Details = styled.div`
  margin-top: 20px;
`

const Discount = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  span {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const SummaryWrapper = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 12px;
  }
`

const SummaryItemWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const SummaryItemTitle = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 4px;
`

const SummaryItemValueWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
`

const SummaryItemValue = styled.span`
  color: ${({ theme, variant }) => variant === 'primary' ? theme.colors.primary : theme.colors.secondary};
  font-size: 18px;
  font-weight: 600;
  margin-right: 5px;
`

const SummaryItemTotal = styled.span`
  color: ${({ theme }) => theme.colors.grey[700]};
  font-size: 12px;
`
